'use client'

import React, { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { Loader2, ChevronDown, ChevronUp, Mail, Bell } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

export type RequestStatusFilter = 'all' | 'pending' | 'read' | 'accepted' | 'declined' | 'canceled'

interface RequestGroup {
  id: number
  counselId: number
  projectTitle: string
  totalCount: number
  pendingCount: number
  acceptedCount: number
  declinedCount: number
  canceledCount: number
  status: 'ACTIVE' | 'CANCELED'
  createdAt: string
  updatedAt: string
}

interface RequestStats {
  targetStats: {
    total: number
    pending: number
    accepted: number
    declined: number
    canceled: number
    pendingOver7Days: number
  }
}

interface GroupRequestItem {
  id: number
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'CANCELED'
  targetType: 'TEAM' | 'INDIVIDUAL'
  targetName?: string
  targetTeamId?: number
  targetProfileId?: number
  respondedAt?: string | null
  canceledAt?: string | null
  createdAt?: string
  updatedAt?: string
}

interface Props {
  projectFilter: number | null
  searchTerm: string
  requestStatusFilter: RequestStatusFilter
  onRequestStatusFilterChange: (v: RequestStatusFilter) => void
  onSendRequest: () => void
  onSendRequestForProject?: (counselId: number) => void
  creditBalance?: number | null
}

const STATUS_LABELS: Record<RequestStatusFilter, string> = {
  all: '전체',
  pending: '대기중',
  read: '열람',
  accepted: '응답 받음',
  declined: '거절',
  canceled: '만료·취소',
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 60) return `${diffMins}분 전`
  if (diffHours < 24) return `${diffHours}시간 전`
  if (diffDays < 7) return `${diffDays}일 전`
  return date.toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function RequestsPipelinePanel({
  projectFilter,
  searchTerm,
  requestStatusFilter,
  onRequestStatusFilterChange,
  onSendRequest,
  onSendRequestForProject,
  creditBalance,
}: Props) {
  const [groups, setGroups] = useState<RequestGroup[]>([])
  const [stats, setStats] = useState<RequestStats['targetStats'] | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        setLoading(true)
        const [groupRes, statRes] = await Promise.all([
          fetch('/api/client/estimate-request-groups?page=1&limit=50'),
          fetch('/api/client/estimate-request-groups/stats'),
        ])

        if (!mounted) return
        const groupJson = groupRes.ok ? await groupRes.json() : { groups: [] }
        const statJson = statRes.ok ? await statRes.json() : { targetStats: null }
        setGroups(groupJson.groups || [])
        setStats(statJson.targetStats || null)
      } finally {
        if (mounted) setLoading(false)
      }
    })()

    return () => {
      mounted = false
    }
  }, [])

  const filtered = useMemo(() => {
    return groups.filter((group) => {
      if (projectFilter && group.counselId !== projectFilter) return false
      const term = searchTerm.trim().toLowerCase()
      if (term && !group.projectTitle.toLowerCase().includes(term)) return false
      if (requestStatusFilter === 'all') return true
      if (requestStatusFilter === 'pending' && group.pendingCount > 0) return true
      if (requestStatusFilter === 'read' && group.pendingCount > 0) return true
      if (requestStatusFilter === 'accepted' && group.acceptedCount > 0) return true
      if (requestStatusFilter === 'declined' && group.declinedCount > 0) return true
      if (requestStatusFilter === 'canceled' && group.canceledCount > 0) return true
      return false
    })
  }, [groups, projectFilter, searchTerm, requestStatusFilter])

  const kpiItems = useMemo(
    () => [
      { key: 'pending' as const, value: stats?.pending ?? 0 },
      { key: 'read' as const, value: stats?.pendingOver7Days ?? 0 },
      { key: 'accepted' as const, value: stats?.accepted ?? 0 },
      { key: 'declined' as const, value: stats?.declined ?? 0 },
      { key: 'canceled' as const, value: stats?.canceled ?? 0 },
    ],
    [stats]
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center py-14">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    )
  }

  return (
    <div className="space-y-4 p-4 md:p-6">
      {/* 1) KPI 요약 카드 (클릭 = 상태 필터) */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {kpiItems.map(({ key, value }) => (
          <button
            key={key}
            type="button"
            onClick={() => onRequestStatusFilterChange(key)}
            className={`rounded-xl border p-3 text-left transition-colors ${
              requestStatusFilter === key
                ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <p className="text-xs text-gray-500">{STATUS_LABELS[key]}</p>
            <p className="text-xl font-bold text-gray-900">{value}</p>
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500">요청 상태는 여기서 추적할 수 있어요</p>

      {/* 2) 프로젝트 리스트 또는 Empty */}
      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center space-y-4">
          <p className="text-base font-semibold text-gray-900">아직 견적 요청을 보낸 프로젝트가 없어요</p>
          <p className="text-sm text-gray-600">
            요청 1건당 1크레딧이 사용되고, 요청 후 상태를 여기서 추적할 수 있어요.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
            <Button onClick={onSendRequest} className="whitespace-nowrap">
              첫 요청 보내기 (1크레딧)
            </Button>
            <Link
              href="/client/projects/new"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              프로젝트 등록하러 가기
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((group) => (
            <ProjectRequestCard
              key={group.id}
              group={group}
              onSendRequest={onSendRequest}
              onSendRequestForProject={onSendRequestForProject}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ProjectRequestCard({
  group,
  onSendRequest,
  onSendRequestForProject,
}: {
  group: RequestGroup
  onSendRequest: () => void
  onSendRequestForProject?: (counselId: number) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [detail, setDetail] = useState<{ requests: GroupRequestItem[] } | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  const loadDetail = async () => {
    if (detail) return
    setLoadingDetail(true)
    try {
      const res = await fetch(`/api/client/estimate-request-groups/${group.id}`)
      if (res.ok) {
        const data = await res.json()
        setDetail({ requests: data.requests || [] })
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleToggle = () => {
    if (!expanded) loadDetail()
    setExpanded(!expanded)
  }

  const hasResponse = group.acceptedCount > 0

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* 2-1) 접힌 상태: 제목/메타 + 액션 */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 p-4 cursor-pointer hover:bg-gray-50/50"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleToggle()}
      >
        <div className="min-w-0 flex-1">
          <p className="text-xs text-gray-500 mb-0.5">프로젝트</p>
          <p className="text-base font-semibold text-gray-900 truncate">{group.projectTitle}</p>
          <p className="text-xs text-gray-500 mt-1">
            요청 {group.totalCount} · 응답 {group.acceptedCount} · 대기 {group.pendingCount}
            {' · '}
            최근 업데이트 {formatRelativeTime(group.updatedAt)}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="whitespace-nowrap text-xs"
            onClick={() => (onSendRequestForProject ? onSendRequestForProject(group.counselId) : onSendRequest())}
          >
            추가 요청 보내기(1크레딧)
          </Button>
          {hasResponse ? (
            <Link
              href={`/client/projects/${group.counselId}/quotes`}
              className="text-sm text-blue-600 font-medium hover:underline whitespace-nowrap"
            >
              응답 보기
            </Link>
          ) : (
            <span
              className="text-sm text-gray-400 cursor-not-allowed whitespace-nowrap"
              title="아직 받은 견적이 없어요"
            >
              응답 보기
            </span>
          )}
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </div>
      </div>

      {/* 2-2) 펼침: 요청 대상 테이블 */}
      {expanded && (
        <div className="border-t border-gray-200 bg-gray-50/50 p-4">
          {loadingDetail ? (
            <div className="py-6 text-center text-sm text-gray-500">불러오는 중...</div>
          ) : detail?.requests?.length ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                      <th className="py-2 pr-2 font-medium">대상</th>
                      <th className="py-2 pr-2 font-medium">채널</th>
                      <th className="py-2 pr-2 font-medium">발송일</th>
                      <th className="py-2 pr-2 font-medium">상태</th>
                      <th className="py-2 text-right font-medium">액션</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.requests.map((req) => {
                      const statusLabel =
                        req.status === 'PENDING'
                          ? '대기중'
                          : req.status === 'ACCEPTED'
                            ? '응답 받음'
                            : req.status === 'DECLINED'
                              ? '거절'
                              : '만료·취소'
                      const targetLabel = req.targetName || (req.targetType === 'TEAM' ? `팀 #${req.targetTeamId}` : `개인 #${req.targetProfileId}`)
                      return (
                        <tr key={req.id} className="border-b border-gray-100">
                          <td className="py-2.5 pr-2">
                            <div className="flex items-center gap-1.5">
                              <Badge
                                variant={req.targetType === 'TEAM' ? 'default' : 'secondary'}
                                className="rounded-full px-2 py-0 text-xs"
                              >
                                {req.targetType === 'TEAM' ? '팀' : '개인'}
                              </Badge>
                              <span className="font-medium text-gray-900">{targetLabel}</span>
                            </div>
                          </td>
                          <td className="py-2.5 pr-2 text-gray-600">
                            <span className="inline-flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5" />
                              이메일
                              <Bell className="w-3.5 h-3.5 text-gray-400" />
                            </span>
                          </td>
                          <td className="py-2.5 pr-2 text-gray-600">
                            {req.createdAt ? formatRelativeTime(req.createdAt) : '-'}
                          </td>
                          <td className="py-2.5 pr-2">
                            <span
                              className={`text-xs font-medium ${
                                req.status === 'PENDING'
                                  ? 'text-amber-700'
                                  : req.status === 'ACCEPTED'
                                    ? 'text-blue-700'
                                    : req.status === 'DECLINED'
                                      ? 'text-red-700'
                                      : 'text-gray-600'
                              }`}
                            >
                              {statusLabel}
                            </span>
                          </td>
                          <td className="py-2.5 text-right">
                            {req.status === 'PENDING' && (
                              <>
                                <button
                                  type="button"
                                  disabled
                                  className="text-xs text-gray-400 mr-2"
                                  title="발송 후 24시간 경과 시 가능"
                                >
                                  리마인드
                                </button>
                                <button
                                  type="button"
                                  className="text-xs text-gray-600 hover:text-red-600"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (confirm('이 요청을 취소할까요?')) {
                                      // TODO: cancel single request API
                                    }
                                  }}
                                >
                                  취소
                                </button>
                              </>
                            )}
                            {req.status === 'ACCEPTED' && (
                              <Link
                                href={`/client/projects/${group.counselId}/quotes`}
                                className="text-xs font-medium text-blue-600 hover:underline"
                              >
                                견적 보기
                              </Link>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-[11px] text-gray-500">
                리마인드는 발송 24시간 후 가능해요 · 요청은 7일 후 만료돼요
              </p>
            </>
          ) : (
            <div className="py-4 text-center text-sm text-gray-500">요청 정보가 없습니다.</div>
          )}
        </div>
      )}
    </div>
  )
}
